import React from 'react';
import type { CertificateStatus, Certificate } from 'features/dashboard/types/dashboard';
import { useNavigate } from 'react-router-dom';
import { useSelectedClient } from '../../hooks/useSelectedClient';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import Skeleton from 'react-loading-skeleton';
import { TabView, TabPanel } from 'primereact/tabview';
import { Paginator } from 'primereact/paginator';
import { useDashboardData } from './hooks/useDashboardData';
import { useCoiRequests } from './hooks/useCoiRequests';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import { useMasterCertificates } from '../master-certificate/hooks/useMasterCertificates';
import { fetchMasterCertificateCoverages, archiveMasterCertificate } from '../master-certificate/api/masterCertificateApi';
import axios from 'axios';
import { handleAxiosError } from 'utilities/apiUtils';
import CertificateCard from './components/CertificateCard';
import RequestsPanel from './RequestsPanel';
import PreviewSendCertificate from '../send-certificate/components/PreviewSendCertificate';
import SentCertificatesPanel from './SentCertificatesPanel';
import RecentActivityPanel from './RecentActivityPanel';
import { useViewMode } from '../../hooks/useViewMode';
import ErrorState from 'components/ErrorState';
import '../../styles/pages.css';


export default function AgentDashboardPage() {
  const { setViewMode } = useViewMode();
  const navigate = useNavigate();
  const toastRef = React.useRef<Toast>(null);

  React.useEffect(() => {
    setViewMode('agent');
  }, [setViewMode]);

  const { hasSelectedClient, insuredId } = useSelectedClient();
  const { data, isLoading, error } = useDashboardData({ enabled: !!hasSelectedClient });
  const {
    requests: pendingRequests,
    isLoading: isPendingLoading,
    error: pendingError,
    approveRequest,
    declineRequest
  } = useCoiRequests(!!hasSelectedClient, insuredId, 'pending');
  const {
    requests: completedRequests,
    isLoading: isCompletedLoading,
    error: completedError
  } = useCoiRequests(!!hasSelectedClient, insuredId, 'completed');

  const [certPageFirst, setCertPageFirst] = React.useState(0);
  const CERT_PAGE_SIZE = 4;

  const [certFilter, setCertFilter] = React.useState<'All' | 'Active'>('Active');
  const [certSearchInput, setCertSearchInput] = React.useState('');
  const [certSearch, setCertSearch] = React.useState('');

  // Debounce search input — 350ms
  React.useEffect(() => {
    const timer = setTimeout(() => setCertSearch(certSearchInput), 350);
    return () => clearTimeout(timer);
  }, [certSearchInput]);

  // Reset to first page when filter or search changes
  React.useEffect(() => { setCertPageFirst(0); }, [certFilter, certSearch]);

  const { certificates: masterCertificates = [], totalCount: masterCertTotal, isLoading: isMasterCertLoading, error: masterCertError, refetch: refetchMasterCerts } = useMasterCertificates(insuredId, {
    skip: certPageFirst,
    top: CERT_PAGE_SIZE,
    activeOnly: certFilter === 'Active',
    search: certSearch,
  });
  const [unreadCount, setUnreadCount] = React.useState<number>(0);
  React.useEffect(() => {
    const fetchUnread = async () => {
      try {
        const token = localStorage.getItem('igtoken');
        if (!token) return;
        const params = new URLSearchParams();
        if (insuredId) params.set('insuredDatabaseId', insuredId);
        const res = await axios.get(`${process.env.REACT_APP_COIPortal_Url}/api/IGIGCOI/activity-unread-count?${params}`, {
          headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
        });
        setUnreadCount(res.data.unreadCount || 0);
      } catch (err) {
        handleAxiosError(err);
        setUnreadCount(0);
      }
    };
    fetchUnread();
  }, [insuredId]);

  const [restoringId, setRestoringId] = React.useState<string | null>(null);
  const [restoreTarget, setRestoreTarget] = React.useState<any | null>(null);

  const [previewVisible, setPreviewVisible] = React.useState(false);
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = React.useState(false);

  const handlePreview = React.useCallback(async (certificateId: string) => {
    if (!insuredId) return;
    setPreviewUrl(null);
    setPreviewVisible(true);
    setIsLoadingPreview(true);
    try {
      const ncToken = localStorage.getItem('NCToken') || '';
      const authToken = localStorage.getItem('igtoken') || '';
      const response = await fetch(`${process.env.REACT_APP_COIPortal_Url}/api/IGIGCOI/preview-certificate`, {
        method: 'POST',
        headers: {
          'NCToken': ncToken,
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify({ insuredDatabaseId: insuredId, certificateId }),
      });
      const data = await response.json();
      if (data.success && data.previewUrl) {
        setPreviewUrl(data.previewUrl);
      } else {
        setPreviewUrl(null);
        toastRef.current?.show({ severity: 'error', summary: 'Preview Failed', detail: 'Could not load certificate preview.', life: 4000 });
      }
    } catch {
      setPreviewUrl(null);
      toastRef.current?.show({ severity: 'error', summary: 'Preview Failed', detail: 'Could not load certificate preview.', life: 4000 });
    } finally {
      setIsLoadingPreview(false);
    }
  }, [insuredId]);

  const confirmRestore = React.useCallback((mc: any) => {
    setRestoreTarget(mc);
  }, []);

  const handleRestore = React.useCallback(async () => {
    if (!restoreTarget || restoringId) return;
    const mc = restoreTarget;
    setRestoreTarget(null);
    setRestoringId(mc.databaseId);
    try {
      await archiveMasterCertificate(mc.databaseId, mc.insuredDatabaseId, true, mc.name);
      refetchMasterCerts();
      toastRef.current?.show({ severity: 'success', summary: 'Restored', detail: `"${mc.name}" has been restored.`, life: 3000 });
    } catch {
      toastRef.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to restore certificate. Please try again.', life: 4000 });
    } finally {
      setRestoringId(null);
    }
  }, [restoreTarget, restoringId, refetchMasterCerts]);

  const mapMasterToCertificate = (mc: any): Certificate => ({
    id: mc.databaseId,
    title: mc.name,
    lineOfBusiness: mc.policies?.length > 0 ? mc.policies[0].linesOfBusiness?.[0] || '' : '',
    certificateNumber: mc.databaseId,
    issuedDateLabel: mc.createDate ? new Date(mc.createDate).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }) : '',
    status: mc.active ? 'Active' : 'Archived',
    icon: 'pi pi-file',
  });

  const pagedCertificates = React.useMemo(
    () => masterCertificates.slice(0, CERT_PAGE_SIZE).map(mapMasterToCertificate),
    [masterCertificates]
  );

  return (
    <div className="page dashboardPage">
      <Toast ref={toastRef} position="top-center" />

      <Dialog
        visible={!!restoreTarget}
        onHide={() => setRestoreTarget(null)}
        className="archiveConfirmDialog"
        modal
        draggable={false}
        resizable={false}
        style={{ width: 380 }}
      >
        <div className="archiveDialog__iconWrap" style={{ background: '#f0fdf4' }}>
          <i className="pi pi-replay archiveDialog__icon" style={{ color: '#15803d' }} />
        </div>
        <div className="archiveDialog__title">Restore Certificate?</div>
        <div className="archiveDialog__message">
          Are you sure you want to restore <strong>"{restoreTarget?.name}"</strong>?
          It will become active again.
        </div>
        <div className="archiveDialog__footer">
          <button className="archiveDialog__cancelBtn p-button" onClick={() => setRestoreTarget(null)}>
            Cancel
          </button>
          <button
            className="archiveDialog__archiveBtn p-button"
            style={{ background: '#15803d', borderColor: '#15803d' }}
            onClick={handleRestore}
          >
            <i className="pi pi-replay" style={{ marginRight: 6, fontSize: 12 }} />
            Yes, Restore
          </button>
        </div>
      </Dialog>

      <PreviewSendCertificate
        visible={previewVisible}
        onHide={() => setPreviewVisible(false)}
        pdfBlobUrl={previewUrl}
        isLoadingPdf={isLoadingPreview}
      />

      <div className="dashboardMasterCerts" style={{ background: '#fff', borderRadius: 16, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', padding: 20, border: '1px solid #e6e7ef' }}>
        <div className="page__sectionHeader">
          {/* Title + count badge */}
          <div className="masterCerts__titleRow">
            <h1 className="page__title" style={{ color: '#381574', fontWeight: 600, margin: 0 }}>Master Certificates</h1>
            <span className="masterCerts__countBadge">{masterCertTotal}</span>
          </div>

          {/* Right-side controls */}
          <div className="masterCerts__controls">
            {/* Combined search + filter bar */}
            <div className="masterCerts__searchBar">
              <i className="pi pi-search masterCerts__searchIcon" />
              <input
                className="masterCerts__searchInput"
                value={certSearchInput}
                onChange={e => setCertSearchInput(e.target.value)}
                placeholder="Search master certificates..."
              />
              {certSearchInput && (
                <i className="pi pi-times masterCerts__clearIcon" onClick={() => setCertSearchInput('')} />
              )}
              <div className="masterCerts__searchDivider" />
              <button
                onClick={() => setCertFilter('All')}
                className={`masterCerts__filterBtn masterCerts__filterBtn--all${certFilter === 'All' ? ' masterCerts__filterBtn--selected' : ''}`}
              >
                All
              </button>
              <button
                onClick={() => setCertFilter('Active')}
                className={`masterCerts__filterBtn masterCerts__filterBtn--status${certFilter === 'Active' ? ' masterCerts__filterBtn--selected' : ''}`}
              >
                Active
              </button>
            </div>

            <Button icon="pi pi-plus" label="Add Master Certificate" className="p-button-sm appButton--primary" onClick={() => navigate('/mastercertificate')} />
            <Button icon="pi pi-send" label="Send Certificate" className="p-button-sm appButton--primary" onClick={() => navigate('/sendcertificate')} />
          </div>
        </div>

        <div className="dashboardCertGrid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 12, minHeight: 120, alignItems: 'stretch', marginTop: 16 }}>
          {isMasterCertLoading
            ? Array.from({ length: 4 }).map((_, index) => (
                <Card key={index} className="certCard" style={{ minWidth: 0 }}>
                  <div className="certCard__body">
                    <div className="certCard__header">
                      <Skeleton width={34} height={34} borderRadius={9} />
                      <Skeleton width={52} height={20} borderRadius={999} />
                    </div>
                    <div className="certCard__content">
                      <Skeleton width="88%" height={12} />
                      <div style={{ marginTop: 5 }}><Skeleton width="55%" height={10} /></div>
                    </div>
                    <div className="certCard__footer">
                      <Skeleton width={44} height={24} borderRadius={7} />
                      <Skeleton width={44} height={24} borderRadius={7} style={{ marginLeft: 4 }} />
                    </div>
                  </div>
                </Card>
              ))
            : masterCertError
            ? <div style={{ gridColumn: '1 / -1' }}><ErrorState compact /></div>
            : pagedCertificates.length === 0
            ? (
              <div style={{ gridColumn: '1 / -1' }} className="panel__emptyState">
                <div className="panel__emptyState__iconWrap">
                  <i className="pi pi-file panel__emptyState__icon" />
                </div>
                <div className="panel__emptyState__title">
                  {certSearch ? `No results for "${certSearch}"` : 'No master certificates'}
                </div>
                <div className="panel__emptyState__sub">
                  {certSearch ? 'Try a different search term.' : 'Click "Add Master Certificate" to create one.'}
                </div>
              </div>
            )
            : pagedCertificates.map((c) => (
              <CertificateCard
                key={c.id}
                certificate={c}
                isRestoring={restoringId === c.id}
                onRestore={c.status === 'Archived' ? () => {
                  const raw = masterCertificates.find(mc => mc.databaseId === c.id);
                  if (raw) confirmRestore(raw);
                } : undefined}
                onEdit={c.status !== 'Archived' ? async () => {
                  const raw = masterCertificates.find(mc => mc.databaseId === c.id);
                  if (!raw) { navigate('/mastercertificate', { state: { editCert: null } }); return; }
                  const freshCoverages = await fetchMasterCertificateCoverages(raw.databaseId);
                  navigate('/mastercertificate', { state: { editCert: { ...raw, coverages: freshCoverages } } });
                } : undefined}
                onPreview={c.status !== 'Archived' ? () => handlePreview(c.id) : undefined}
                onSend={c.status !== 'Archived' ? () => {
                  navigate('/sendcertificate', { state: { initialMasterCertId: c.id } });
                } : undefined}
              />
            ))}
        </div>
        {masterCertTotal > CERT_PAGE_SIZE && (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12 }}>
            <Paginator
              first={certPageFirst}
              rows={CERT_PAGE_SIZE}
              totalRecords={masterCertTotal}
              onPageChange={(e: { first: number }) => setCertPageFirst(e.first)}
              template="PrevPageLink PageLinks NextPageLink"
              className="requests-paginator"
            />
          </div>
        )}
      </div>

      <Card className="pageCard">
        <TabView className="dashboardTabView">
          
          <TabPanel header="Certificates">
            {insuredId && (
              <SentCertificatesPanel
                apiUrl={`${process.env.REACT_APP_COIPortal_Url}/api/IGIGCOI/sent-certificates`}
                token={localStorage.getItem('igtoken') || ''}
                insuredDatabaseId={insuredId}
              />
            )}
          </TabPanel>
          <TabPanel header="Requests">
            <RequestsPanel
              isPendingLoading={isPendingLoading}
              pendingError={pendingError}
              pendingRequests={pendingRequests}
              isCompletedLoading={isCompletedLoading}
              completedError={completedError}
              completedRequests={completedRequests}
              approveRequest={approveRequest}
              declineRequest={declineRequest}
            />
          </TabPanel>
          <TabPanel header={
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              Activity
              {unreadCount > 0 && (
                <span style={{ background: '#ef4444', color: '#fff', borderRadius: 10, padding: '1px 6px', fontSize: 10, fontWeight: 700, lineHeight: '16px', minWidth: 18, textAlign: 'center', display: 'inline-block' }}>
                  {unreadCount}
                </span>
              )}
            </span>
          }>
            <RecentActivityPanel />
          </TabPanel>
        </TabView>
      </Card>
    </div>
  );
}
